import { createAsyncAction, createReducer, ActionType } from 'typesafe-actions';
import {
    FetchAction,
    SuccessFetchActionPayload,
    DefaultErrorPayload,
    defaultRequestHandler,
    defaultErrorHandler,
} from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import { GenericShopApiResponse } from '../../../common/apiAdapters/types';

export interface ThreeDImageState {
    isFetching: boolean;
    modelSRC?: string;
    identifier: string;
    didInvalidate: boolean;
}

export const defaultThreeDImageState: ThreeDImageState = {
    didInvalidate: false,
    identifier: '',
    isFetching: false,
};

interface ThreeDImageApiEntry {
    id: string;
    name: string;
    androidAR?: {
        file: {
            url: string;
        };
    };
}

export type ThreeDImageAPIResponse = GenericShopApiResponse<Array<ThreeDImageApiEntry>>;

export const asyncActions = createAsyncAction(
    '@PRODUCT_DETAILS_THREE_D_MODEL/FETCH_START',
    '@PRODUCT_DETAILS_THREE_D_MODEL/FETCH_SUCCESS',
    '@PRODUCT_DETAILS_THREE_D_MODEL/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<ThreeDImageAPIResponse>, DefaultErrorPayload>();

export const actions = [asyncActions.failure, asyncActions.request, asyncActions.success];

export type ThreeDImageActionsType =
    | ActionType<typeof asyncActions.request>
    | ActionType<typeof asyncActions.success>
    | ActionType<typeof asyncActions.failure>;

export const reducer = createReducer<ThreeDImageState, ThreeDImageActionsType>(defaultThreeDImageState)
    .handleAction(asyncActions.success, (state, action) => {
        if (state.identifier !== action.payload.identifier) {
            return state;
        }
        const modelSRC = action.payload.data.data[0]?.androidAR?.file.url;
        return {
            ...state,
            identifier: action.payload.identifier,
            isFetching: false,
            didInvalidate: false,
            modelSRC,
        };
    })
    .handleAction(asyncActions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(asyncActions.request, (state, action) => defaultRequestHandler(state, action));

export const fetchThreeDImage = (sku?: string): DataFetchingThunkAction => async (
    dispatch,
    getState,
    { apiService, logger }
) => {
    const identifier = sku;
    if (!sku || !identifier || identifier === getState().productdetails.threeDImage.identifier) {
        return;
    }
    dispatch(asyncActions.request({ identifier }));
    try {
        const result = await apiService.shopApi.getThreeDImage(sku);
        if (result.success) {
            dispatch(asyncActions.success({ identifier, data: result }));
        } else if (result.errors && result.errors.length > 1) {
            dispatch(asyncActions.failure({ identifier, error: result.errors[0] }));
        } else {
            dispatch(asyncActions.failure({ identifier, error: 'unknown api error getting three d image' }));
        }
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'threeDImage');
        dispatch(asyncActions.failure({ error: err, identifier }));
    }
};
